'use client';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { COLORS, Logo, MaterialIcon, MenuButton, MenuButtonModel, Transition } from '@repo/ui';
import Link from 'next/link';
import { useSelectedLayoutSegments } from 'next/navigation';
import { MaterialIconType } from '../materialIcon/types';

export type MenuModel = {
  buttonList: MenuButtonModel[];
  headerTitle?: string;
  headerContent?: React.JSX.Element;
  headerButtons?: { action: any; text: string; icon?: MaterialIconType }[];
  userData?: string[];
  closeSession?: () => void;
  closeMenu?: () => void;
  showContactData?: boolean;
  collapsedMenu?: boolean;
  setCollapsedMenu?: (_: boolean) => void;
};

/**
 * Renders the commerce navigation menu component.
 * @param {MenuModel} props - The component properties.
 *
 * @return {React.JSX.Element} The rendered Menu component.
 */

export const Menu = ({
  buttonList,
  headerTitle,
  headerContent,
  headerButtons,
  userData,
  closeSession,
  closeMenu,
  showContactData = false,
  collapsedMenu,
  setCollapsedMenu,
}: MenuModel) => {
  // Filters Next.js sections to access real segments
  const segment = useSelectedLayoutSegments().filter((str) => !/\(.*?\)/.test(str));
  const isDesktop = useMediaQuery('(min-width: 1200px)', { noSsr: true });
  const isShort = useMediaQuery('(max-height: 700px)', { noSsr: true });
  const desktopcollapsedMenu = isDesktop && collapsedMenu;
  return (
    <Stack direction={'row'} sx={{ height: '100%', overflowY: 'auto' }}>
      <Stack
        data-testid="menu"
        // sx={{ height: '100%', width: '100%', py: { xs: 2, lg: isShort ? 0 : 4 }, px: 2 }}
        sx={{ height: '100%', width: '100%', px: 2, py: 0 }}
        spacing={isShort ? 2 : 4}
      >
        {closeMenu && (
          <Grid container justifyContent={'flex-end'} sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <IconButton data-testid="menu-close" onClick={closeMenu}>
              <MaterialIcon icon="close" />
            </IconButton>
          </Grid>
        )}

        <Grid
          width="100%"
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          flexDirection={'column'}
          gap={isShort ? 1 : 2}
          height={'100%'}
          sx={{ transition: 'max-height 0.2s cubic-bezier(0, 0, 0.2, 1) 0.2s' }}
          maxHeight={desktopcollapsedMenu ? '60px' : '160px'}
          p={0}
          // pt={{ xs: isShort ? 0 : 2, lg: isShort ? 0 : 8 }}
          // pb={{ xs: 0, lg: desktopcollapsedMenu ? (isShort ? 3 : 6) : 0 }}
        >
          <Tooltip title={desktopcollapsedMenu && headerTitle ? headerTitle : ''} placement="right" arrow>
            <Box height={desktopcollapsedMenu ? 38 : 25}>
              <Logo height="100%" type={desktopcollapsedMenu ? 'small' : 'normal'} />
            </Box>
          </Tooltip>

          {headerTitle && !desktopcollapsedMenu && (
            <Transition type="fadeDown" delay={0.2}>
              <Typography variant="h2" color={COLORS.primary.blue.main} textAlign="center" sx={{ minWidth: '230px' }}>
                {headerTitle}
              </Typography>
            </Transition>
          )}
        </Grid>
        {headerContent && <>{headerContent}</>}

        {headerButtons &&
          headerButtons.map((headerButton, ix) => (
            <Tooltip key={ix} title={desktopcollapsedMenu ? headerButton.text : ''} placement="right" arrow>
              <Button onClick={headerButton.action} sx={{ gap: 0.5, minWidth: 0, width: '100%', whiteSpace: 'nowrap' }}>
                {headerButton.icon && <MaterialIcon icon={headerButton.icon} />}
                <Transition type="fadeExpand">{!desktopcollapsedMenu && headerButton.text}</Transition>
              </Button>
            </Tooltip>
          ))}
        <Divider />
        <Stack gap={isShort ? 1 : 2} sx={{ flex: 1 }}>
          {buttonList?.map((button, ix) => (
            <Tooltip key={ix} title={desktopcollapsedMenu ? button.text : ''} placement="right" arrow>
              <Link href={`/${button.title}`}>
                <MenuButton
                  icon={button.icon}
                  text={desktopcollapsedMenu ? undefined : button.text}
                  active={segment[0] === button.title}
                  sx={{ minWidth: 0, ...button.sx }}
                />
              </Link>
            </Tooltip>
          ))}
        </Stack>
        <Divider />
        <Stack gap={isShort ? 0 : 1} justifyContent="center" sx={{ mb: '16px !important' }}>
          {showContactData && (
            // <Stack
            //   textAlign="left"
            //   justifyContent="flex-start"
            //   width="100%"
            //   paddingLeft={2}
            //   gap={desktopcollapsedMenu ? 2 : 0}
            // >
            <>
              <Tooltip title={desktopcollapsedMenu ? 'ayuda@directo.com.ar' : ''} placement="right" arrow>
                <MenuButton
                  icon="mail"
                  text={desktopcollapsedMenu ? '' : 'ayuda@directo.com.ar'}
                  onClick={() => window.open('mailto:ayuda@directo.com.ar')}
                  sx={{ color: COLORS.text.placeholder }}
                />
              </Tooltip>
              <Tooltip title={desktopcollapsedMenu ? '0800 345 6790' : ''} placement="right" arrow>
                <MenuButton
                  icon="call"
                  text={desktopcollapsedMenu ? '' : '0800 345 6790'}
                  onClick={() => window.open('tel:08003456790')}
                  sx={{ color: COLORS.text.placeholder }}
                />
                {/* <Box display={'flex'} gap={1} alignItems={'center'} sx={{ minWidth: '230px' }}>
                  <MaterialIcon icon="call" size={desktopcollapsedMenu ? 24 : 16} />
                  {!desktopcollapsedMenu && (
                    <Transition type="fade" delay={0.2}>
                      <Typography variant="caption" color={COLORS.text.placeholder}>
                        0800 345 6790
                      </Typography>
                    </Transition>
                  )}
                </Box> */}
              </Tooltip>
            </>

            // </Stack>
          )}
          <Tooltip title={desktopcollapsedMenu ? 'Cerrar sesión' : ''} placement="right" arrow>
            <MenuButton
              icon="logout"
              text={desktopcollapsedMenu ? '' : 'Cerrar sesión'}
              onClick={closeSession}
              sx={{ color: 'red' }}
              // sx={{ color: 'red', mb: desktopcollapsedMenu ? 0 : 2 }}
            />
          </Tooltip>

          {userData && (
            <>
              {/* {desktopcollapsedMenu && (
                <Transition type="fadeDown"> */}
              <Tooltip
                title={
                  desktopcollapsedMenu ? (
                    <List>
                      {userData?.map((data, ix) => (
                        <ListItem key={ix}>{data}</ListItem>
                      ))}
                    </List>
                  ) : (
                    ''
                  )
                }
                placement="right"
                arrow
              >
                <MaterialIcon
                  icon="account_circle"
                  sx={{
                    pl: 2,
                    color: COLORS.text.placeholder,
                    overflow: 'hidden',
                    height: desktopcollapsedMenu ? 'auto' : '0 !important',
                    lineHeight: desktopcollapsedMenu ? 'inherit' : '0 !important',
                    opacity: desktopcollapsedMenu ? '1 !important' : '0 !important',
                    transition: 'all 0.3s cubic-bezier(0, 0, 0.2, 1) !important',
                  }}
                />
              </Tooltip>
              {/* </Transition>
              )} */}
              <Stack
                gap={1}
                paddingLeft={2}
                sx={{
                  transition: 'max-height 0.2s cubic-bezier(0, 0, 0.2, 1)',
                  overflow: 'hidden',
                  minWidth: '230px',
                  maxHeight: desktopcollapsedMenu ? '0px' : '230px',
                }}
              >
                {userData?.map((data, ix) => (
                  <Typography key={ix} variant="caption" color={COLORS.text.placeholder}>
                    <span>{data}</span>
                  </Typography>
                ))}
              </Stack>
            </>
          )}

          {/* {userData && !desktopcollapsedMenu ? (
            <Transition type="fadeUp" delay={0.2}>
              <Stack gap={1} paddingLeft={2} sx={{ minWidth: '230px' }}>
                {userData?.map((data, ix) => (
                  <Typography key={ix} variant="caption" color={COLORS.text.placeholder}>
                    <span>{data}</span>
                  </Typography>
                ))}
              </Stack>
            </Transition>
          ) : (
            <Tooltip
              title={
                desktopcollapsedMenu ? (
                  <List>
                    {userData?.map((data, ix) => (
                      <ListItem key={ix}>{data}</ListItem>
                    ))}
                  </List>
                ) : (
                  ''
                )
              }
              placement="right"
              arrow
            >
              <MaterialIcon icon="account_circle" sx={{ pl: 2, color: COLORS.text.placeholder }} />
            </Tooltip>
          )} */}
        </Stack>
      </Stack>
      {setCollapsedMenu && isDesktop && (
        <MaterialIcon
          pointer
          onClick={() => {
            document.getElementsByClassName('MuiTooltip-popper').length === 0 &&
              setCollapsedMenu(!desktopcollapsedMenu);
          }}
          // icon={desktopcollapsedMenu ? 'chevron_right' : 'chevron_left'}
          icon={'keyboard_arrow_up'}
          size={13}
          sx={{
            transform: desktopcollapsedMenu ? 'rotate(90deg)' : 'rotate(270deg)',
            transition: 'all 0.2s cubic-bezier(0, 0, 0.2, 1) !important',
            outline: 'solid 1px #E9DCFF',
            left: desktopcollapsedMenu ? 78 : 268,
            top: '50%',
            textAlign: 'center',
            position: 'absolute',
            margin: 'auto',
            backgroundColor: 'white',
            borderRadius: '50%',
            height: 20,
            width: 20,
          }}
        />
      )}
    </Stack>
  );
};
